import React, {useCallback, useMemo, useRef} from 'react'
import * as Sentry from '@sentry/browser'
import { NetworkStatus } from 'apollo-client'
import { ToastContainer } from 'react-toastify'

import Cart from '../Cart/Cart'
import RestaurantHeader from '../RestaurantHeader/RestaurantHeader'
import NavScroller from '../NavScroller/NavScroller'
import CartButton from '../CartButton/CartButton'
import Dollars from '../Dollars/Dollars'
import { GetCartQuery } from '../CartQuery/CartQuery'
import ScrollToTop from '../ScrollToTop/ScrollToTop'
import { useAvailability } from '../../utils/availability'
import { getQuoteTime } from '../../utils/quote-time-helpers'

import MenuPageError from './MenuPage.error'
import MenuPageLayout from './MenuPageLayout/MenuPageLayout'
import MenuPageLoader from './MenuPage.loader'
import style from './MenuPage.css'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useOutpostRestaurant } from '../OutpostProvider/OutpostProvider'
import { lifecycle } from '../../utils/lifecycle'
import { useHasMode } from '../ModeRouter/ModeRouter'
import { PoweredByToast } from '../PoweredByToast/PoweredByToast'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'
import { useMenus } from '../../hooks/use-menus'
import { Menus } from '../Menus/Menus'
import { XP_SINGLE_PAGE_MENU } from '../ExperimentsProvider/experiment-definitions'
import { useVariant } from '../../hooks/use-variant'

const { toastContainer, toastWrapper } = style

const MenuPage = () => {
  const variant = useVariant(XP_SINGLE_PAGE_MENU.experimentName)
  const showAllMenus = variant === 1
  const { restaurantInfo, getRestaurantPath, restaurantGuid, shortUrl } = useRestaurant()
  const onCompleted = () => {
    lifecycle.trigger('menuloaded', { restaurantGuid })
  }
  const { fulfillmentTime } = useFulfillment()
  const { outpostDeliveryDateTime, outpostLoading } = useOutpostRestaurant()
  const dateTime = outpostDeliveryDateTime || fulfillmentTime || undefined
  const variables = { input: { shortUrl, dateTime } }
  const { loading, error, menus, refetch, networkStatus } = useMenus({
    onCompleted: onCompleted,
    notifyOnNetworkStatusChange: true
  })

  const retry = () => {
    if (error) refetch()
    if (restaurantInfo.error) {
      restaurantInfo.refetch()
    }
  }

  const activeMenu = useMemo(() => {
    return menus && menus.length
      ? menus.find(({ isActive }) => isActive) || menus[0]
      : { groups: [] }
  }, [menus])
  const allGroups = useMemo(() => {
    if (menus && menus.length) {
      if (showAllMenus) {
        return menus.reduce((acc, { groups }) => [...acc, ...groups], [])
      }
      if (activeMenu && activeMenu.groups) {
        return activeMenu.groups
      }
    }
    return []
  }, [activeMenu, menus, showAllMenus])
  const availability = useAvailability()
  const isCartMode = useHasMode('cart')
  const restaurantHeaderPublicApi = useRef()

  const calcHeaderOffset = useCallback(async () => {
    const offset = restaurantHeaderPublicApi.current ? await restaurantHeaderPublicApi.current.calcHeaderOffset() : 0
    return offset
  }, [])

  if (
    loading ||
    availability.loading ||
    networkStatus === NetworkStatus.refetch ||
    outpostLoading ||
    restaurantInfo.loading
  ) {
    return <MenuPageLoader />
  }
  if (error || restaurantInfo.error) {
    return <MenuPageError retry={retry} restaurantInfo={restaurantInfo.data} />
  }

  let { orderingAvailable } = availability
  if (!menus || !activeMenu) {
    Sentry.withScope(scope => {
      scope.setLevel(Sentry.Severity.Warning)
      scope.setExtra('queryVars', variables)
      Sentry.captureMessage('Data is null for Menus Query')
    })
    return <MenuPageError retry={refetch} />
  }
  const restaurant = {
    ...restaurantInfo.data.restaurant,
    minimumTakeoutTime: getQuoteTime(restaurantInfo.data)
  }

  return (
    <React.Fragment>
      <ScrollToTop />
      <NavScroller calcOffsetFn={calcHeaderOffset} items={allGroups}>
        {({ items, activeItem, refMap, imperativeItemHandler }) => {
          items = items.filter(
            item => item.items && item.items.length > 0
          )
          return (
            <MenuPageLayout
              header={
                <RestaurantHeader
                  restaurant={restaurant}
                  menus={activeMenu}
                  activeItem={activeItem}
                  imperativeItemHandler={imperativeItemHandler}
                  publicApi={restaurantHeaderPublicApi}
                />
              }
              menus={
                <React.Fragment>
                  <Menus menus={menus} refMap={refMap} menuGroups={items} showAllMenus={showAllMenus} />
                  <div styleName='footer'>
                    <PoweredByToast styleName='poweredBy' />
                  </div>
                </React.Fragment>
              }
              cart={
                <Cart
                  orderingAvailable={orderingAvailable}
                  checkoutLink={getRestaurantPath('checkout')}
                />
              }
            />
          )
        }}
      </NavScroller>

      <GetCartQuery>
        {({ error, loading, cart }) => {
          if (
            !error &&
            !loading &&
            cart &&
            cart.order &&
            cart.order.numberOfSelections > 0 &&
            !isCartMode
          ) {
            return (
              <div styleName='cartButton'>
                <CartButton
                  to={getRestaurantPath('cart')}
                  left={
                    <span>
                    View Cart ({cart.order.numberOfSelections})
                    </span>
                  }
                  right={<Dollars amount={cart.order.total} />}
                />
              </div>
            )
          }
          return null
        }}
      </GetCartQuery>
      <ToastContainer
        hideProgressBar
        closeButton={false}
        className={toastContainer}
        toastClassName={toastWrapper}
      />
    </React.Fragment>
  )
}

export default MenuPage
